/**
 * The share link, permanently visible once a route has been saved.
 *
 * Originally this only appeared in a dialog on first save, which meant the link
 * was unrecoverable the moment you dismissed it — or if you reopened a route you
 * had saved earlier. The link is the entire point of saving, so it belongs on
 * the page, not behind a one-shot modal.
 */

import { useEffect, useRef, useState } from 'react';

export interface ShareBarProps {
  routeId: string;
  dirty: boolean;
}

export function ShareBar({ routeId, dirty }: ShareBarProps) {
  const url = `${window.location.origin}/r/${routeId}`;
  const [copied, setCopied] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  // Clear the confirmation if the link changes underneath it.
  useEffect(() => setCopied(false), [routeId]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API needs a secure context and can be blocked outright.
      // Selecting the text means ⌘C still works.
      inputRef.current?.select();
    }
  }

  return (
    <section className="share-bar">
      <div className="share-bar-head">
        <span className="share-bar-label">Share link</span>
        {dirty && <span className="share-bar-stale">unsaved changes</span>}
      </div>

      <div className="share-row">
        <input
          ref={inputRef}
          readOnly
          value={url}
          onFocus={(event) => event.target.select()}
          aria-label="Share link"
        />
        <button type="button" onClick={copy}>
          {copied ? 'Copied' : 'Copy'}
        </button>
      </div>

      <div className="share-bar-actions">
        <a href={`/r/${routeId}`} target="_blank" rel="noreferrer">
          Preview
        </a>
        <a href={`/api/routes/${routeId}/gpx`}>Download GPX</a>
        <code className="share-bar-id">{routeId}</code>
      </div>
    </section>
  );
}
